import { ChangeDetectionStrategy, Component, DestroyRef, inject, linkedSignal, OnInit, signal, ViewChild } from '@angular/core';
import { API, ca_am_Role_retrieveAll_order, mm_MediumGroup_retrieveAll_order, Order, RoleDTO } from '../../../services/api/api';
import { OrderOption, SbSplitButton, SplitButtonClass } from '../../../services/global/global-interfaces';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { RoleEditModalComponent } from './edit/role-edit-modal.component';
import { ToasterService, ToastType } from '../../../services/toaster/toaster.service';
import { RequestClass } from '../../../services/global/RequestClass';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { PermissionService } from '../../../services/permission.service';

type SplitButtonAction = 'edit';
type RoleDTOAndButtons = SplitButtonClass<RoleDTO, SplitButtonAction>;

@Component({
  selector: 'page-client-role-management',
  templateUrl: './client-role-management.component.html',
  styleUrls: ['./client-role-management.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientRoleManagementComponent extends RequestClass implements OnInit {
  // injects
  private readonly api = inject(API);
  public readonly modal = inject(ModalService);
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly translate = inject(TranslateService);
  private readonly toasterService = inject(ToasterService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly permissionService = inject(PermissionService);

  // global reference imports
  protected readonly window = window;
  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;
  protected readonly ca_am_Role_retrieveAll_order = ca_am_Role_retrieveAll_order;

  /* filter variables */
  // search
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';
  // toggles
  owned: boolean | null = null;

  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  /* ordering */
  orderOption = signal<OrderOption<ca_am_Role_retrieveAll_order>>({
    direction: Order.asc,
    value: ca_am_Role_retrieveAll_order.NAME,
  });
  filterPanelOrderOption = linkedSignal<OrderOption<ca_am_Role_retrieveAll_order>, OrderOption<ca_am_Role_retrieveAll_order>>({
    source: this.orderOption,
    computation: orderOption => {
      return { ...orderOption };
    },
  });
  allOrderOptions: OrderOption<ca_am_Role_retrieveAll_order>[] = [
    { value: ca_am_Role_retrieveAll_order.NAME, direction: Order.asc, label: this.translate.instant('lang.globals.name') },
    { value: ca_am_Role_retrieveAll_order.ID, direction: Order.asc, label: this.translate.instant('lang.globals.Id') },
  ];

  /* global */
  roles = signal<RoleDTO[]>([]);
  modalOpen: boolean = false;

  constructor() {
    super();
    this.resetOrderOption();
  }

  ngOnInit(): void {}

  // order methods
  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ ...this.allOrderOptions[0] });
  }

  setToOrderAndRefresh(pValue: ca_am_Role_retrieveAll_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  setToOrderFilterPanel(pValue: ca_am_Role_retrieveAll_order) {
    this.filterPanelOrderOption.update(orderOption => {
      orderOption.value = pValue;
      return { ...orderOption };
    });
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  setOrderDirectionFilterPanel(value: Order) {
    this.filterPanelOrderOption.update(orderOption => {
      orderOption.direction = value;
      return { ...orderOption };
    });
    if (this.filterPanelRef) {
      this.filterPanelRef.hasExternalChanges.set(true);
    }
  }

  private setToOrderOption(pValue: ca_am_Role_retrieveAll_order) {
    if (!pValue) {
      // nothing is set fallback to default
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...this.allOrderOptions.find(option => (option.value = pValue)) });
    }
  }

  getOrder(): { by: ca_am_Role_retrieveAll_order; direction: Order } {
    // if no orderOption -> return default order
    const currentOrderOption = this.orderOption();
    if (!currentOrderOption.value) {
      this.resetOrderOption();
      return {
        by: ca_am_Role_retrieveAll_order.NAME,
        direction: Order.desc,
      };
    }
    return { by: currentOrderOption.value, direction: currentOrderOption.direction };
  }

  // request methods
  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    this.api
      .ca_am_Role_retrieveAll(
        {
          canAddUser: true,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          order: this.getOrder(),
          owned: this.owned,
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (data: RoleDTO[]) => {
          this.roles.set(this.buildSplitButtons(data));
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  buildSplitButtons<
    T extends {
      custom: boolean;
    },
  >(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.role_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.editRole',
          action: 'edit',
          disabled: !item.custom,
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(role: RoleDTOAndButtons, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.editRole(role);
        break;

      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  createRole() {
    this.modalOpen = true;
    this.modal
      .showNavigationModal({
        entryComponent: { value: RoleEditModalComponent, data: {} },
        title: this.translate.instant('lang.globals.createRole'),
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((roleCreated: boolean) => {
        this.modalOpen = false;
        if (roleCreated) {
          this.refreshFromServer();
          this.toasterService.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.roleCreated'));
        }
      })
      .catch(() => {
        this.modalOpen = false;
      });
  }

  editRole(role: RoleDTOAndButtons) {
    if (!role) {
      throw new Error('Missing role');
    }
    this.modalOpen = true;
    this.modal
      .showNavigationModal({
        entryComponent: { value: RoleEditModalComponent, data: { roleID: role.id } },
        title: this.translate.instant('lang.globals.editRole'),
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((roleEdited: boolean) => {
        this.modalOpen = false;
        if (roleEdited) {
          this.refreshFromServer();
          this.toasterService.toast(ToastType.SUCCESS, this.translate.instant('lang.globals.changesSaved'));
        }
      })
      .catch(() => {
        this.modalOpen = false;
      });
  }

  // filter panel methods
  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.owned = filterMap.get('companies')?.[0];
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.orderOption.set({ ...this.filterPanelOrderOption() });
  }
}
